import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Schedule, Booking, Participant } from '../../models';
import { getSchedule, claimBooking } from '../../data/api';
import MultiParticipant from '../../components/MultiParticipant';
import toast from '../../components/Toast';

export default function ClaimBooking() {

  const navigate = useNavigate();
  const { bookingId = '' } = useParams<{ bookingId: string }>();

  const [event, setEvent] = useState<Schedule | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [guardianFirstName, setGuardianFirstName] = useState<string | null>(null);
  const [guardianLastName, setGuardianLastName] = useState<string | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [underAgeCheck, setUnderAgeCheck] = useState<boolean>(true);
  const [phone, setPhone] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [x: string]: boolean }>({});

  const submitHandler = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (participants.length > 0) {
      setLoading(true);

      const bookingRecord: Booking = {
        scheduleId: bookingId,
        email: (email || '').toLowerCase(),
        participant: participants,
        guardianFirstName: guardianFirstName || '',
        guardianLastName: guardianLastName || '',
        telephone: phone || '',
        underAge: underAgeCheck,
        paid: false
      };

      const submitStatus = await claimBooking(bookingId, bookingRecord);

      if (submitStatus?.success) {
        toast.success(submitStatus?.message || '');
        navigate(`/book/${moment(event?.startDateTimeUTC).local().format('YYYYMMDD')}`);
      } else {
        toast.error(submitStatus?.message || '');
      }
      setLoading(false);
    } else {
      setErrors({
        participants: true
      });
    }
  };

  useEffect(() => {
    const getScheduleInfo = async () => {
      if (bookingId) {
        const scheduleInfo = await getSchedule(bookingId);
        if (scheduleInfo.success) {
          setEvent(scheduleInfo.data || null);
        }
      }
    };
    getScheduleInfo();
  }, []);

  return (
    <div id="schedule-claim-booking-container">
      <div className="col-md-7 col-lg-7 col-sm-12 mb-4">
        <h1>Claim Booking</h1>

        <Form onSubmit={submitHandler} className="my-4 pb-4">
          <fieldset disabled={loading}>
            <Form.Group className="mb-4">
              <h5 className="fullimpact-grey">Details</h5>
              <hr />
              <Form.Group as={Row} controlId="plainTextStart">
                <Form.Label column sm="2">Start</Form.Label>
                <Col sm="10">
                  <Form.Control
                    plaintext
                    readOnly
                    defaultValue={event?.startDateTimeUTC ? moment(event.startDateTimeUTC).local().format('LLLL') : ''}
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="mb-3" controlId="plainTextEnd">
                <Form.Label column sm="2">End</Form.Label>
                <Col sm="10">
                  <Form.Control
                    plaintext
                    readOnly
                    defaultValue={event?.endDateTimeUTC ? moment(event.endDateTimeUTC).local().format('LLLL') : ''}
                  />
                </Col>
              </Form.Group>
              <hr />
            </Form.Group>


            <h5 className="fullimpact-grey pt-4">Sign Up</h5>
            <hr />

            <MultiParticipant
              onChange={(updatedParticipants: Participant[]) => {
                setErrors({
                  ...errors,
                  participants: updatedParticipants.length === 0
                });
                setParticipants(updatedParticipants);
              }}
              isInvalid={!!errors.participants}
            />

            <Form.Group className="mb-3 pb-3" controlId="underAgeCheck">
              <Form.Check type="checkbox" aria-label="under age check">
                <Form.Check.Input type="checkbox" checked={underAgeCheck} onChange={() => setUnderAgeCheck(!underAgeCheck)} />
                <Form.Check.Label className="ms-2">Particpant Under 18</Form.Check.Label>
              </Form.Check>

              <Form.Text className="text-muted">
                If you are under 18, a parent/guradian is required to sign up on the participants behalf.
                By checking this box, you are acknowledging you are the parent/guardian of the associated particpant.
              </Form.Text>
            </Form.Group>

            {underAgeCheck && (
              <Form.Group as={Row} className="mb-3">
                <Col sm="5">
                  <Form.Group className="mb-3" controlId="guardianFirstName">
                    <Form.Label>Guardian First Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="First Name"
                      value={guardianFirstName || ''}
                      onChange={(e) => setGuardianFirstName(e.target.value)}
                      isInvalid={false}
                      required={underAgeCheck}
                    />
                  </Form.Group>
                </Col>
                <Col sm="5">
                  <Form.Group className="mb-3" controlId="guardianLastName">
                    <Form.Label>Guardian Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Last Name"
                      value={guardianLastName || ''}
                      onChange={(e) => setGuardianLastName(e.target.value)}
                      isInvalid={false}
                      required={underAgeCheck}
                    />
                  </Form.Group>
                </Col>
              </Form.Group>
            )}

            <hr />

            <Form.Group className="mb-3 col-sm-10" controlId="email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Email"
                value={email || ''}
                onChange={(e) => setEmail(e.target.value)}
                isInvalid={false}
                required
              />
              <Form.Text className="text-muted">
                please use a valid email for confirmation
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="phone">
              <Form.Label>Phone</Form.Label>
              <Row>
                <Col sm={6}>
                  <Form.Control
                    type="tel"
                    placeholder="Phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    isInvalid={false}
                    pattern="[0-9]{10}"
                  />
                </Col>
              </Row>
              <Form.Text className="text-muted">
                <i>optional <small className="px-2">(format: #########)</small></i> - used to reach out for urgent booking updates
              </Form.Text>
              <hr />
            </Form.Group>

            <div className="d-grid gap-2 mt-4">
              <Button
                size="lg"
                variant="primary"
                type="submit"
                className="mt-4 d-flex justify-content-center align-items-center"
              >
                <span>Book</span>
                {loading && <Spinner animation="border" variant="light" size="sm" className="ms-2" />}
              </Button>
            </div>
          </fieldset>

        </Form>
      </div>
    </div>
  );
}